import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { PiCheck, PiHouseLine, PiPlus, PiShareNetwork, PiUserCircle } from 'react-icons/pi';
import { useFeedback } from '../../FeedbackContext';
import { getAccountDisplayName, getAccountRoleLabel, getAccountTypeKey, getAccountTypeLabel } from '../../utils/accountPresentation';
import BottomSheet from '../BottomSheet';
import './styles.css';

const DESKTOP_QUERY = '(min-width: 1180px)';
const POPOVER_WIDTH = 340;
const VIEWPORT_GAP = 12;

const getPopoverPosition = (anchorEl) => {
  const anchorRect = anchorEl?.getBoundingClientRect?.();
  const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
  const width = Math.min(POPOVER_WIDTH, viewportWidth - (VIEWPORT_GAP * 2));
  const preferredLeft = anchorRect ? anchorRect.right - width : (viewportWidth - width) / 2;

  return {
    width,
    left: Math.max(VIEWPORT_GAP, Math.min(preferredLeft, viewportWidth - width - VIEWPORT_GAP)),
    top: anchorRect ? anchorRect.bottom + 8 : 72,
  };
};

const AccountSwitcherContent = ({
  accounts,
  currentAccountId,
  currentUser,
  loading,
  selectingId,
  onSelect,
  onCreateAccount,
}) => (
  <div className="account-switcher__content">
    <div className="account-switcher__list" role="list" aria-busy={loading}>
      {accounts.map((account) => {
        const selected = account.id === currentAccountId;
        const selecting = selectingId === account.id;
        const accountType = getAccountTypeKey(account, currentUser?.uid);
        const AccountIcon = accountType === 'family'
          ? PiHouseLine
          : accountType === 'shared'
            ? PiShareNetwork
            : PiUserCircle;

        return (
          <button
            type="button"
            role="listitem"
            key={account.id}
            className={`account-switcher__item${selected ? ' is-selected' : ''}`}
            disabled={Boolean(selectingId)}
            onClick={() => onSelect(account.id)}
            aria-current={selected ? 'true' : undefined}
            aria-busy={selecting}
          >
            <span className={`account-switcher__icon account-switcher__icon--${accountType}`} aria-hidden="true"><AccountIcon /></span>
            <span className="account-switcher__copy">
              <strong>{getAccountDisplayName(account)}</strong>
              <small>{getAccountTypeLabel(account, currentUser?.uid)} · {getAccountRoleLabel(account.role)}</small>
            </span>
            <span className="account-switcher__check" aria-label={selected ? 'Conta atual' : undefined}>
              {selected ? (
                <><PiCheck aria-hidden="true" /><small>Ativa</small></>
              ) : selecting ? (
                <><span className="account-switcher__spinner" aria-hidden="true" /><small>Trocando</small></>
              ) : null}
            </span>
          </button>
        );
      })}
      {loading && accounts.length === 0 && <p className="account-switcher__empty" role="status">Carregando suas contas...</p>}
      {!loading && accounts.length === 1 && accounts[0].id === currentAccountId && (
        <p className="account-switcher__empty">Você ainda não participa de outras contas.</p>
      )}
      {!loading && accounts.length === 0 && (
        <p className="account-switcher__empty">Não foi possível encontrar suas contas.</p>
      )}
    </div>
    {onCreateAccount && (
      <button type="button" className="account-switcher__create" onClick={onCreateAccount} disabled={Boolean(selectingId)}>
        <PiPlus aria-hidden="true" />
        Criar conta familiar
      </button>
    )}
  </div>
);

const AccountSwitcherPopover = ({ children, anchorEl, onClose }) => {
  const titleId = `account-switcher-title-${useId().replace(/:/g, '')}`;
  const popoverRef = useRef(null);
  const previousFocusRef = useRef(document.activeElement);
  const [position, setPosition] = useState(() => getPopoverPosition(anchorEl));

  const updatePosition = useCallback(() => {
    setPosition(getPopoverPosition(anchorEl));
  }, [anchorEl]);

  useEffect(() => {
    const previousFocus = previousFocusRef.current;
    updatePosition();
    window.requestAnimationFrame(() => popoverRef.current?.focus({ preventScroll: true }));

    const handlePointerDown = (event) => {
      if (popoverRef.current?.contains(event.target) || anchorEl?.contains?.(event.target)) return;
      onClose();
    };
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onClose();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      previousFocus?.focus?.({ preventScroll: true });
    };
  }, [anchorEl, onClose, updatePosition]);

  return createPortal(
    <section
      ref={popoverRef}
      className="account-switcher-popover"
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      tabIndex={-1}
      style={{ top: position.top, left: position.left, width: position.width }}
    >
      <header className="account-switcher-popover__header">
        <div>
          <h2 id={titleId}>Trocar conta</h2>
          <p>Escolha qual espaço financeiro deseja visualizar.</p>
        </div>
      </header>
      {children}
    </section>,
    document.body
  );
};

const AccountSwitcher = ({ accounts, currentAccountId, currentUser, loading, onSelect, onClose, onCreateAccount, anchorEl }) => {
  const [selectingId, setSelectingId] = useState(null);
  const [desktop, setDesktop] = useState(() => window.matchMedia(DESKTOP_QUERY).matches);
  const { showError } = useFeedback();

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_QUERY);
    const handleChange = (event) => setDesktop(event.matches);
    setDesktop(mediaQuery.matches);
    mediaQuery.addEventListener?.('change', handleChange);
    return () => mediaQuery.removeEventListener?.('change', handleChange);
  }, []);

  const handleSelect = async (accountId) => {
    if (accountId === currentAccountId || selectingId) return;
    setSelectingId(accountId);
    try {
      await onSelect(accountId);
      onClose();
    } catch (selectionError) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Falha ao trocar conta:', {
          stage: 'select-account',
          accountId,
          code: selectionError?.code || selectionError?.message || 'unknown',
          message: selectionError?.message || 'Falha desconhecida',
        });
      }
      const message = selectionError.message === 'ACCOUNT_ACCESS_DENIED'
        ? 'Você não possui acesso a esta conta.'
        : 'Não foi possível trocar de conta. Tente novamente.';
      showError(message, 'Troca de conta não concluída');
    } finally {
      setSelectingId(null);
    }
  };

  const content = (
    <AccountSwitcherContent
      accounts={accounts}
      currentAccountId={currentAccountId}
      currentUser={currentUser}
      loading={loading}
      selectingId={selectingId}
      onSelect={handleSelect}
      onCreateAccount={onCreateAccount}
    />
  );

  if (desktop) {
    return <AccountSwitcherPopover anchorEl={anchorEl} onClose={onClose}>{content}</AccountSwitcherPopover>;
  }

  return (
    <BottomSheet
      title="Trocar conta"
      subtitle="Escolha qual espaço financeiro deseja visualizar."
      onClose={onClose}
      size="sm"
      className="account-switcher"
    >
      {content}
    </BottomSheet>
  );
};

const contentPropTypes = {
  accounts: PropTypes.array.isRequired,
  currentAccountId: PropTypes.string,
  currentUser: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  selectingId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onCreateAccount: PropTypes.func,
};

AccountSwitcherContent.propTypes = contentPropTypes;
AccountSwitcherPopover.propTypes = {
  children: PropTypes.node.isRequired,
  anchorEl: PropTypes.any,
  onClose: PropTypes.func.isRequired,
};
AccountSwitcher.propTypes = {
  accounts: PropTypes.array.isRequired,
  currentAccountId: PropTypes.string,
  currentUser: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreateAccount: PropTypes.func,
  anchorEl: PropTypes.any,
};

export default AccountSwitcher;